import { useState } from "react";
import { Button } from "./ui/button";
import { Link2Icon, CopyIcon } from "@radix-ui/react-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "./ui/use-toast";

interface ShareDialogProps {
  assessmentId: string;
  assessmentLink: string;
  onShare?: () => void; // Optional callback after successful share
  compact?: boolean; // Optional flag for compact display
  directCopy?: boolean; // Optional flag to directly copy without opening dialog
}

export function ShareDialog({
  assessmentId,
  assessmentLink,
  onShare,
  compact = false,
  directCopy = false,
}: ShareDialogProps) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const handleCopyLink = async () => {
    setError("");
    setIsLoading(true);

    try {
      await navigator.clipboard.writeText(assessmentLink);

      toast({
        title: "Link copied",
        description: "Assessment link copied to clipboard",
        variant: "default",
      });

      // Call the onShare callback if provided
      if (onShare) {
        onShare();
      }

      setOpen(false);
    } catch (err: any) {
      setError("Failed to copy link to clipboard");
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (directCopy) {
    return (
      <Button 
        variant="outline" 
        size={compact ? "sm" : "default"}
        onClick={handleCopyLink}
        disabled={isLoading}
        icon={<CopyIcon className="h-4 w-4" />}
      >
        {isLoading ? "Copying..." : "Copy link"}
      </Button>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
      }}
    >
      <DialogTrigger asChild>
        {compact ? (
          <Button 
            variant="outline" 
            size="sm"
            icon={<CopyIcon className="h-4 w-4" />}
          >
            Copy link
          </Button>
        ) : (
          <Button 
            variant="outline"
            icon={<Link2Icon className="h-4 w-4" />}
          >
            Share
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share assessment</DialogTitle>
          <DialogDescription>
            Copy the link below to share this assessment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="link">Assessment link</Label>
            <div className="flex space-x-2">
              <Input
                id="link"
                value={assessmentLink}
                readOnly
                className="flex-1"
              />
              <Button 
                onClick={handleCopyLink} 
                disabled={isLoading}
                icon={<CopyIcon className="h-4 w-4" />}
              >
                {isLoading ? "Copying..." : "Copy"}
              </Button>
            </div>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
